import { ChangeEvent, KeyboardEvent, MouseEvent, useRef } from 'react';
import {
  Button,
  CloseIcon,
  ConfirmationDialog,
  DocIcon,
  DocxIcon,
  PdfIcon,
  TrashIcon,
  YellowWarningIcon,
  confirmDialog,
} from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { ALLEGATION_DELETE_CONFIRM } from '@/constants';
import { ToolbarChipButton } from './ToolbarChipButton';

// =============================================================================
// File-type icons + tile-wash helpers
// =============================================================================

const getFileExtension = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
};

const ICON_HEIGHT = 20;
const PDF_ICON_WIDTH = Math.round((ICON_HEIGHT * 222) / 195);
const DOC_ICON_WIDTH = Math.round((ICON_HEIGHT * 231) / 195);
const DOCX_ICON_WIDTH = Math.round((ICON_HEIGHT * 291) / 195);

const FileTypeIcon = ({ filename }: { filename: string }) => {
  const ext = getFileExtension(filename);
  if (ext === 'pdf') return <PdfIcon width={PDF_ICON_WIDTH} height={ICON_HEIGHT} />;
  if (ext === 'docx') return <DocxIcon width={DOCX_ICON_WIDTH} height={ICON_HEIGHT} />;
  if (ext === 'doc') return <DocIcon width={DOC_ICON_WIDTH} height={ICON_HEIGHT} />;
  return <DocIcon width={DOC_ICON_WIDTH} height={ICON_HEIGHT} />;
};

const getInkRgb = (filename: string) => (getFileExtension(filename) === 'pdf' ? '231, 74, 74' : '74, 126, 231');

const getTileWashStyle = (filename: string) => {
  const ink = getInkRgb(filename);
  return {
    background: [
      `radial-gradient(120% 110% at 12% 18%, rgba(${ink}, 0.14) 0%, rgba(${ink}, 0.02) 55%, rgba(255, 255, 255, 0) 75%)`,
      `radial-gradient(110% 120% at 88% 86%, rgba(${ink}, 0.12) 0%, rgba(${ink}, 0.02) 50%, rgba(255, 255, 255, 0) 80%)`,
      '#ffffff',
    ].join(', '),
  };
};

const getTileBorderColor = (filename: string) => (getFileExtension(filename) === 'pdf' ? '#E74A4A' : '#4A7EE7');

// =============================================================================
// Main component
// =============================================================================

export interface CaseBackgroundDocItem {
  /** Stable id used as React key + passed to the delete handler. */
  id: string;
  /** Display name (also drives the file-type icon + tile colour). */
  name: string;
  /** When set, an upload progress overlay (0–100) is shown over the tile. */
  uploadProgress?: number;
}

export interface CaseBackgroundSectionProps {
  /** Documents to render as tiles. */
  documents: CaseBackgroundDocItem[];
  /** When false, the dashed Upload tile and the trash buttons are hidden
   * (read-only mode). The sidebar gates this on access control + status. */
  canEdit: boolean;
  /** Click handler for the dashed Upload tile — opens the file picker. */
  onUploadClick: () => void;
  /** Called with the doc id when the user confirms a delete. */
  onDelete: (id: string) => void;
  /** Selected files from the file picker — sidebar handles the actual upload. */
  onFilesSelected: (files: FileList) => void;
  /** Optional aria-label for the dashed Upload tile. */
  uploadAriaLabel?: string;
  /**
   * Optional download handler. When passed, a download button appears in
   * the top-right of each tile on hover — sits to the left of the trash
   * button (when in edit mode) or on its own (in read-only mode). Left as
   * opt-in so the same section can be reused without a download surface
   * in future callers.
   */
  onDownload?: (id: string) => void;
}

/**
 * Reusable A4-portrait grid of case-background documents — DOC/DOCX/PDF
 * tiles with a soft coloured wash, hover-reveal trash button, and a
 * dashed "Upload" tile at the end. Purely presentational: the parent
 * (e.g. CreateEditCaseSidebar) owns all data and fires the handlers.
 */
export const CaseBackgroundSection = ({
  documents,
  canEdit,
  onUploadClick,
  onDelete,
  onFilesSelected,
  uploadAriaLabel = 'Upload allegation document',
  onDownload,
}: CaseBackgroundSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddDocumentClick = () => {
    fileInputRef.current?.click();
    onUploadClick();
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    onFilesSelected(event.target.files);
    event.target.value = '';
  };

  const handleDeleteClick = async (id: string, event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const confirmed = await confirmDialog({ id: ALLEGATION_DELETE_CONFIRM, allegationDocumentId: id });
    if (confirmed) {
      onDelete(id);
    }
  };

  // Fires when the user clicks anywhere on the tile — the whole tile acts
  // as a "click to download" affordance now. The delete button uses
  // `stopPropagation` (see its handler) so hover-clicking Delete doesn't
  // also trigger a download.
  const handleTileClick = (id: string) => {
    onDownload?.(id);
  };

  const handleTileKeyDown = (id: string, event: KeyboardEvent<HTMLLIElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onDownload?.(id);
    }
  };

  return (
    <>
      {/* Allegation-document delete confirmation — same warning-icon look
          and narrow Delete/Cancel button bar used on MedicalRecordsPage
          when deleting a document. */}
      <ConfirmationDialog
        id={ALLEGATION_DELETE_CONFIRM}
        style={{ width: '280px', borderRadius: '8px', border: '1px solid #e1e1e0', overflow: 'hidden' }}
        contentClassName="!p-0"
        headerClassName="!hidden"
      >
        {(_params, { accept, reject }) => (
          <div className="relative p-[24px]">
            <button
              type="button"
              aria-label="Close"
              onClick={reject}
              className="absolute top-[24px] right-[24px] cursor-pointer rounded p-1 focus:outline-none"
            >
              <CloseIcon width={12} height={12} />
            </button>
            <div className="mt-[16px] flex flex-col items-center">
              <YellowWarningIcon width={48} height={48} />
              <div className="text-fletchers-black-900 mt-[16px] text-[18px]/[18px] font-[600]">Are you sure?</div>
            </div>
            <div className="text-fletchers-black-900 mt-[24px] text-center text-[14px]/[20px]">
              The selected document will be permanently deleted.
            </div>
            <div className="mt-[24px] flex gap-[8px]">
              <Button
                outlined
                label="Cancel"
                className="hover:bg-fletchers-gray-110 flex-1"
                onClick={reject}
                style={{ height: '32px', fontSize: '14px', borderRadius: '4px' }}
              />
              <Button
                label="Delete"
                className="flex-1 [&:hover]:!bg-[#C42E2E]"
                onClick={accept}
                style={{
                  height: '32px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  backgroundColor: '#E13C3C',
                  borderColor: '#E13C3C',
                  color: 'white',
                }}
              />
            </div>
          </div>
        )}
      </ConfirmationDialog>

      {/* A4-portrait grid: 3 columns, equal-width tiles. Each tile uses
          `aspect-[1/1.414]` (the ISO A-series ratio) so they read as
          portrait pages regardless of how wide the column ends up. */}
      <ul className="grid grid-cols-3 gap-2">
        {documents.map((doc) => {
          // Whole-tile download affordance. Only wired when the parent
          // passed `onDownload`; otherwise the tile is inert (as before).
          const isDownloadable = !!onDownload;
          return (
            <li
              key={doc.id}
              style={{
                ...getTileWashStyle(doc.name),
                ['--tile-hover-border' as string]: getTileBorderColor(doc.name),
              }}
              role={isDownloadable ? 'button' : undefined}
              tabIndex={isDownloadable ? 0 : undefined}
              aria-label={isDownloadable ? `Download ${doc.name}` : undefined}
              title={isDownloadable ? 'Click to download' : undefined}
              onClick={isDownloadable ? () => handleTileClick(doc.id) : undefined}
              onKeyDown={isDownloadable ? (event) => handleTileKeyDown(doc.id, event) : undefined}
              className={classNames(
                'group border-fletchers-gray-110 relative flex aspect-[1/1.414] flex-col overflow-hidden rounded-md border p-2 hover:[border-color:var(--tile-hover-border)]',
                isDownloadable && 'cursor-pointer',
              )}
            >
              {/* Delete button — top-right on hover. Only rendered in edit
                  mode. `stopPropagation` inside the handler stops the tile's
                  own click firing a download when the user hits Delete. */}
              {canEdit && (
                <ToolbarChipButton
                  aria-label={`Delete ${doc.name}`}
                  title="Delete"
                  onClick={(event) => handleDeleteClick(doc.id, event)}
                  className="absolute top-1 right-1 z-10 hidden shadow-sm group-hover:flex"
                >
                  <TrashIcon className="[&>path]:fill-fletchers-black-900 h-3 w-3" />
                </ToolbarChipButton>
              )}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 backdrop-blur-[3px] backdrop-saturate-150"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0"
                style={{
                  height: '75%',
                  background:
                    'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 30%, rgba(255,255,255,0) 100%)',
                }}
              />
              <div className="relative flex w-full justify-center pt-6">
                <FileTypeIcon filename={doc.name} />
              </div>
              <span
                className="text-fletchers-black-900 relative mt-auto line-clamp-3 w-full text-left text-xs leading-tight font-semibold break-words lowercase"
                title={doc.name}
              >
                {doc.name}
              </span>
              {/* Upload progress overlay — sits above everything when the
                file is still streaming to Azure. Sidebar drives the
                value via uploadList; we just render the bar. The track
                spans the full width and hugs the tile's bottom edge —
                the tile's `overflow-hidden` + `rounded-md` clip its
                corners so it lines up with the rounded border. Fill
                colour mirrors the tile's accent (red for pdf, blue for
                doc/docx) so the bar matches the surrounding outline. */}
              {typeof doc.uploadProgress === 'number' && doc.uploadProgress < 100 && (
                <div className="absolute inset-x-0 bottom-0 z-20 h-0.5 overflow-hidden bg-white/60">
                  <div
                    className="h-full transition-[width] duration-150"
                    style={{ width: `${doc.uploadProgress}%`, backgroundColor: getTileBorderColor(doc.name) }}
                  />
                </div>
              )}
            </li>
          );
        })}
        {canEdit && (
          <li>
            <button
              type="button"
              aria-label={uploadAriaLabel}
              onClick={handleAddDocumentClick}
              className="border-fletchers-gray-110 hover:border-fletchers-green-600 hover:text-fletchers-green-600 text-fletchers-gray-200 flex aspect-[1/1.414] w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed bg-white"
            >
              <i className="pi pi-arrow-up text-[16px]" aria-hidden />
              <span className="text-[14px] font-semibold">Upload</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".doc,.docx,.pdf"
              multiple
              className="hidden"
              onChange={handleFileInputChange}
            />
          </li>
        )}
      </ul>
    </>
  );
};
